// Today's events from Wikipedia's "On this day" feed.
// Text is CC BY-SA, so the window links back to Wikipedia for attribution.

const FETCH_TIMEOUT_MS = 10000;

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

const pad = (n) => String(n).padStart(2, '0');

// the same feed is served from two hosts; try the second if the first fails
const feedUrls = (mm, dd) => [
  `https://en.wikipedia.org/api/rest_v1/feed/onthisday/events/${mm}/${dd}`,
  `https://api.wikimedia.org/feed/v1/wikipedia/en/onthisday/events/${mm}/${dd}`
];

export function dayInfo(date = new Date()) {
  const month = date.getMonth() + 1;
  const day = date.getDate();
  const monthName = MONTH_NAMES[date.getMonth()];
  return {
    mm: pad(month),
    dd: pad(day),
    label: `${monthName} ${day}`,
    pageUrl: `https://en.wikipedia.org/wiki/${monthName}_${day}`
  };
}

export const formatYear = (year) => (year < 0 ? `${-year} BC` : String(year));

// only ever link to Wikipedia itself
function wikipediaUrl(url) {
  try {
    const parsed = new URL(url);
    return parsed.protocol === 'https:' && /(^|\.)wikipedia\.org$/.test(parsed.hostname) ? parsed.href : null;
  } catch (error) {
    return null;
  }
}

// -> [{ year, text, links: [{ title, url }] }], newest first
export function parseEvents(json) {
  const events = Array.isArray(json?.events) ? json.events : [];
  return events
    .filter(event => Number.isFinite(event?.year) && typeof event.text === 'string' && event.text.trim() !== '')
    .map(event => ({
      year: event.year,
      text: event.text.trim(),
      links: (Array.isArray(event.pages) ? event.pages : [])
        .map(page => ({
          title: String(page?.normalizedtitle || page?.title || '').replace(/_/g, ' '),
          url: wikipediaUrl(page?.content_urls?.desktop?.page)
        }))
        .filter(link => link.title && link.url)
        .slice(0, 2)
    }))
    .sort((a, b) => b.year - a.year);
}

async function getJson(url, fetchFn) {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), FETCH_TIMEOUT_MS);
  try {
    const response = await fetchFn(url, { signal: controller.signal, headers: { Accept: 'application/json' } });
    if (!response.ok) throw new Error(`Wikipedia answered ${response.status}`);
    return await response.json();
  } finally {
    clearTimeout(timer);
  }
}

const cacheKey = (info) => `onthisday:${info.mm}-${info.dd}`;

function readCache(info, storage) {
  try {
    const cached = JSON.parse(storage.getItem(cacheKey(info)));
    return Array.isArray(cached) ? cached : null;
  } catch (error) {
    return null;
  }
}

function writeCache(info, events, storage) {
  try {
    storage.setItem(cacheKey(info), JSON.stringify(events));
  } catch (error) {
    /* storage unavailable or full */
  }
}

// Resolves with the parsed events; rejects if neither host could be reached.
export async function fetchOnThisDay(info, { fetchFn = (...args) => fetch(...args), storage = window.sessionStorage } = {}) {
  const cached = readCache(info, storage);
  if (cached) return cached;

  let lastError = new Error('No Wikipedia host answered');
  for (const url of feedUrls(info.mm, info.dd)) {
    try {
      const events = parseEvents(await getJson(url, fetchFn));
      writeCache(info, events, storage);
      return events;
    } catch (error) {
      lastError = error;
    }
  }
  throw lastError;
}
